/**
 * SEO ve yapay zekâ (v0.1.190; Sercan 16 Oct 2026: "hangi anahtar kelimeler test ediliyor ve ne durumdayız göremiyorum"):
 * on top where we stand this week, then the twenty buyer questions of the weekly AI check, each with its search words,
 * this week's answer per engine and its weeks as marks (open a row for the full question, week by week, and who was
 * named in our place), the sites that take our place, and the words, pages and countries that showed the site on
 * Google and Bing over 28 days against the 28 before. Engines are named by what they are, never by model.
 */

import { type ReactNode, useEffect, useState } from 'react';
import {
  SEO_CHANGE_STATE_LABELS,
  SEO_CHANGE_VERDICT_LABELS,
  SEO_ENGINE_LABELS,
  SEO_GAP_KIND_LABELS,
  SEO_SEARCH_DAYS,
  SEO_TOP_RANK,
  type SeoChange,
  type SeoCheck,
  type SeoEngine,
  type SeoQuestion,
  type SeoReportView,
  type SeoSearch,
  type SeoSearchRow,
  type SeoSearchWeek,
  type SeoWeek,
} from '../api/seo-report.js';
import { usePanel } from '../app/context.js';
import { useIsDesktop } from '../app/media.js';
import { useSeoReport } from '../app/seo-report.js';
import { formatIsoDay, formatNumber, formatRelativeClock } from '../lib/format.js';
import { Icon } from './icons.js';
import { AiFoundLines, WeeklyBars } from './seo-charts.js';

/** The engines the page shows, in this order; Google search only once its key is in (no rows otherwise). */
const ENGINES: readonly SeoEngine[] = ['gemini', 'brave', 'google'];
const ROWS_FIRST = 25;

const COUNTRY_TR: Readonly<Record<string, string>> = Object.freeze({
  aus: 'Avustralya',
  nzl: 'Yeni Zelanda',
  usa: 'ABD',
  can: 'Kanada',
  gbr: 'Birleşik Krallık',
  irl: 'İrlanda',
  zaf: 'Güney Afrika',
  tur: 'Türkiye',
  deu: 'Almanya',
  fra: 'Fransa',
  nld: 'Hollanda',
  ita: 'İtalya',
  esp: 'İspanya',
  swe: 'İsveç',
  nor: 'Norveç',
  pol: 'Polonya',
  ind: 'Hindistan',
  tha: 'Tayland',
  phl: 'Filipinler',
  idn: 'Endonezya',
  mys: 'Malezya',
  sgp: 'Singapur',
  are: 'BAE',
  sau: 'Suudi Arabistan',
  bra: 'Brezilya',
  mex: 'Meksika',
  arg: 'Arjantin',
  chl: 'Şili',
  jpn: 'Japonya',
});

function n(value: number): string {
  return formatNumber(value);
}

function pos(value: number | null): string {
  return value === null ? '-' : formatNumber(value, 1);
}

function pct(value: number | null): string {
  return value === null ? '-' : `%${formatNumber(value * 100, 1)}`;
}

function pathOf(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?cadgaragepro\.com/, '') || '/';
}

/** "+12" / "-3" against the period before; nothing when both are zero. */
function Delta({ now, before }: { now: number; before: number }) {
  if (now === 0 && before === 0) return null;
  const diff = now - before;
  if (before === 0) return <span className="seo-delta seo-delta--new">yeni</span>;
  if (diff === 0) return <span className="seo-delta">=</span>;
  return (
    <span
      className={diff > 0 ? 'seo-delta seo-delta--up' : 'seo-delta seo-delta--down'}
    >{`${diff > 0 ? '+' : ''}${n(diff)}`}</span>
  );
}

/** A smaller position is better: an arrow up when it moved closer to the top. */
function PosDelta({ now, before }: { now: number | null; before: number | null }) {
  if (now === null || before === null) return null;
  const diff = before - now;
  if (Math.abs(diff) < 0.5) return null;
  return (
    <span className={diff > 0 ? 'seo-delta seo-delta--up' : 'seo-delta seo-delta--down'}>
      {diff > 0 ? `↑${formatNumber(diff, 1)}` : `↓${formatNumber(-diff, 1)}`}
    </span>
  );
}

/** One engine's answer for one question and week, in a few words. */
function checkWords(check: SeoCheck | undefined): {
  text: string;
  tone: 'found' | 'near' | 'none' | 'error' | 'empty';
} {
  if (check === undefined) return { text: '-', tone: 'empty' };
  if (check.status === 'error') return { text: 'cevap yok', tone: 'error' };
  if (check.engine === 'gemini') {
    if (check.mentioned === true && check.cited === true) return { text: 'anıldı + kaynak', tone: 'found' };
    if (check.cited === true) return { text: 'kaynak gösterildi', tone: 'found' };
    if (check.mentioned === true) return { text: 'anıldı', tone: 'found' };
    return { text: 'yokuz', tone: 'none' };
  }
  if (check.rank === null) return { text: 'ilk 100’de yokuz', tone: 'none' };
  return { text: `${check.rank}. sıra`, tone: check.rank <= SEO_TOP_RANK ? 'found' : 'near' };
}

function Mark({ check, week }: { check: SeoCheck | undefined; week: string }) {
  const words = checkWords(check);
  return (
    <span className={`seo-mark seo-mark--${words.tone}`} title={`${formatIsoDay(week)}: ${words.text}`}>
      <span className="sr-only">{`${formatIsoDay(week)}: ${words.text}`}</span>
    </span>
  );
}

function checkOf(question: SeoQuestion, week: string | undefined, engine: SeoEngine): SeoCheck | undefined {
  if (week === undefined) return undefined;
  return question.checks.find((check) => check.weekStart === week && check.engine === engine);
}

function Card({ id, title, note, children }: { id: string; title: string; note?: ReactNode; children: ReactNode }) {
  const headingId = `seo-${id}-h`;
  return (
    <article className="au-card sx-card" aria-labelledby={headingId}>
      <header className="au-card__head">
        <div className="au-card__titles">
          <h3 id={headingId} className="au-card__name">
            {title}
          </h3>
          {note === undefined ? null : <p className="au-card__purpose">{note}</p>}
        </div>
      </header>
      {children}
    </article>
  );
}

function weekOf(summary: readonly SeoWeek[], week: string | undefined, engine: SeoEngine): SeoWeek | undefined {
  return week === undefined ? undefined : summary.find((row) => row.weekStart === week && row.engine === engine);
}

/**
 * v0.1.213 (Sercan, 19 Oct 2026: "yapay zekada çıktığımız sorguları göremiyorum"): the newest week's questions that
 * found us, one line per engine hit: the question as asked, where we stood and the page of ours, first the AI answers.
 */
function FoundQueries({ view }: { view: SeoReportView }) {
  const week = view.weeks.at(-1);
  if (week === undefined) return null;
  // v0.1.259: a question that names us always "finds" us; those answers have their own section.
  const hits = view.questions.flatMap((question) =>
    (question.pool === 'brand' ? [] : ENGINES).flatMap((engine) => {
      const check = checkOf(question, week, engine);
      return check?.found === true ? [{ question, engine, check }] : [];
    }),
  );
  const order = (engine: SeoEngine) => ENGINES.indexOf(engine);
  hits.sort((a, b) => order(a.engine) - order(b.engine) || (a.check.rank ?? 0) - (b.check.rank ?? 0));
  return (
    <section aria-labelledby="seo-found-h" className="sx-section">
      <h2 id="seo-found-h" className="sx-section__title">
        Bizi bulan sorgular ({formatIsoDay(week)} haftası)
      </h2>
      {hits.length === 0 ? (
        <p className="sx-empty">Bu hafta hiçbir soruda çıkmadık.</p>
      ) : (
        <div className="seo-scroll">
          <table className="sx-table seo-table seo-found">
            <caption>Bu hafta bizi bulan sorular</caption>
            <thead>
              <tr>
                <th scope="col">Soru ya da aranan</th>
                <th scope="col">Nerede</th>
                <th scope="col">Sonuç</th>
                <th scope="col">Çıkan sayfamız</th>
              </tr>
            </thead>
            <tbody>
              {hits.map(({ question, engine, check }) => (
                <tr key={`${question.id}-${engine}`}>
                  <th scope="row">
                    {engine === 'gemini' ? question.ask || question.label : `"${question.query || question.label}"`}
                    {question.pool === 'rotating' ? <span className="seo-chip">bu haftanın sorusu</span> : null}
                  </th>
                  <td>{SEO_ENGINE_LABELS[engine]}</td>
                  <td className="mono">
                    {engine === 'gemini'
                      ? check.mentioned && check.cited
                        ? 'anıldı, kaynak gösterildi'
                        : check.cited
                          ? 'kaynak gösterildi'
                          : 'anıldı'
                      : check.rank === null
                        ? '-'
                        : `${n(check.rank)}. sıra`}
                  </td>
                  <td className="seo-table__page mono">
                    {check.hitUrl === null ? (
                      '-'
                    ) : !/^https?:\/\//i.test(check.hitUrl) ? (
                      check.hitUrl
                    ) : (
                      <a href={check.hitUrl} target="_blank" rel="noreferrer noopener">
                        {check.hitUrl.replace(/^https?:\/\/(www\.)?/, '')}
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * v0.1.259 (Sercan, 2 Nov 2026): what an assistant says when a buyer asks about CadGaragePro by name, and which sites
 * it learned it from; and where our own pages stand when our name is searched.
 */
function BrandAnswers({ view }: { view: SeoReportView }) {
  const week = view.weeks.at(-1);
  const brand = view.questions.filter((question) => question.pool === 'brand');
  if (week === undefined || brand.length === 0) return null;
  return (
    <section aria-labelledby="seo-brand-h" className="sx-section">
      <h2 id="seo-brand-h" className="sx-section__title">
        Adımızla sorulunca ({formatIsoDay(week)} haftası)
      </h2>
      <div className="seo-scroll">
        <table className="sx-table seo-table">
          <caption>Yapay zekâ cevabının başı, kaynakları ve adımızla aramada sıramız</caption>
          <thead>
            <tr>
              <th scope="col">Soru</th>
              <th scope="col">Yapay zekâ ne dedi</th>
              <th scope="col">Kaynaklar</th>
              <th scope="col">Brave'de sıramız</th>
            </tr>
          </thead>
          <tbody>
            {brand.map((question) => {
              const answer = checkOf(question, week, 'gemini');
              const search = checkOf(question, week, 'brave');
              return (
                <tr key={question.id}>
                  <th scope="row">{question.ask}</th>
                  <td>
                    {answer === undefined || answer.status !== 'ok'
                      ? '-'
                      : `${answer.mentioned ? '' : 'Bizi tanımadı. '}${answer.detail ?? ''}`}
                  </td>
                  <td className="mono">{answer?.topDomains.join(', ') || '-'}</td>
                  <td className="mono">
                    {search === undefined || search.status !== 'ok'
                      ? '-'
                      : search.rank === null
                        ? 'ilk 20 içinde yok'
                        : `${n(search.rank)}. sıra`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Summary({
  view,
  loading,
  error,
  onReload,
}: {
  view: SeoReportView;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}) {
  const clock = usePanel().now;
  const last = view.weeks.at(-1);
  const before = view.weeks.at(-2);
  const engines = ENGINES.filter((engine) => view.summary.some((row) => row.engine === engine));
  const core = view.questions.filter((question) => question.pool === 'core');
  const total = core.filter((question) => question.ask !== '').length;
  const found = core.filter((question) =>
    engines.some((engine) => checkOf(question, last, engine)?.found === true),
  ).length;
  return (
    <section className="au-sum drafting" aria-labelledby="seo-sum-h">
      <h2 id="seo-sum-h" className="au-sum__title">
        {last === undefined ? (
          'Yapay zekâ testi henüz çalışmadı'
        ) : (
          <>
            {formatIsoDay(last)} haftası: <span className="mono accent">{n(found)}</span> / {n(total)} soruda
            bulunuyoruz
          </>
        )}
      </h2>
      <dl className="au-sum__grid">
        {engines.map((engine) => {
          const now = weekOf(view.summary, last, engine);
          const prev = weekOf(view.summary, before, engine);
          return (
            <div key={engine}>
              <dt>{SEO_ENGINE_LABELS[engine]}</dt>
              <dd className="mono">
                {now === undefined || now.asked === 0 ? 'cevap yok' : `${n(now.found)} / ${n(now.asked)}`}
                {prev === undefined || prev.asked === 0 ? null : (
                  <span className="ink-3"> (önceki {n(prev.found)})</span>
                )}
              </dd>
            </div>
          );
        })}
        <div>
          <dt>Google gösterim ({SEO_SEARCH_DAYS} gün)</dt>
          <dd className="mono">
            {view.google === null ? '-' : n(view.google.impressions)}
            {view.google === null ? null : (
              <span className="ink-3"> (önceki {n(view.google.previousImpressions)})</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Google tıklama</dt>
          <dd className="mono">
            {view.google === null ? '-' : n(view.google.clicks)}
            {view.google === null ? null : <span className="ink-3"> (önceki {n(view.google.previousClicks)})</span>}
          </dd>
        </div>
        <div>
          <dt>Google ortalama sıra</dt>
          <dd className="mono">
            {view.google === null ? '-' : pos(view.google.position)}
            {view.google === null ? null : <span className="ink-3"> (önceki {pos(view.google.previousPosition)})</span>}
          </dd>
        </div>
      </dl>
      <p className="au-sum__meta">
        <span>
          Yapay zekâ testi her pazartesi; arama verisi her sabah · güncellendi{' '}
          {formatRelativeClock(Date.parse(view.generatedAt), clock)}
        </span>
        <button type="button" className="btn btn--secondary btn--small" onClick={onReload} disabled={loading}>
          <Icon name="refresh-cw" size={14} />
          {loading ? 'Yenileniyor…' : 'Yenile'}
        </button>
      </p>
      {error === null ? null : (
        <p className="au-sum__error" role="status">
          Yenilenemedi: {error}
        </p>
      )}
    </section>
  );
}

function QuestionRow({
  question,
  weeks,
  engines,
}: {
  question: SeoQuestion;
  weeks: readonly string[];
  engines: readonly SeoEngine[];
}) {
  const last = weeks.at(-1);
  const lastChecks = engines.map((engine) => checkOf(question, last, engine));
  const others = [...new Set(lastChecks.flatMap((check) => check?.topDomains ?? []))]
    .filter((domain) => !domain.endsWith('cadgaragepro.com'))
    .slice(0, 4);
  return (
    <li className="seo-q">
      <details>
        <summary className="seo-q__summary">
          <span className="seo-q__titles">
            <span className="seo-q__label">{question.label}</span>
            <span className="seo-q__query mono">{question.query === '' ? question.id : `"${question.query}"`}</span>
          </span>
          <span className="seo-q__engines">
            {engines.map((engine, index) => {
              const words = checkWords(lastChecks[index]);
              return (
                <span key={engine} className="seo-q__engine">
                  <span className="seo-q__engine-name">{SEO_ENGINE_LABELS[engine]}</span>
                  <span className={`seo-pill seo-pill--${words.tone}`}>{words.text}</span>
                  <span className="seo-q__marks" aria-hidden="true">
                    {weeks.map((week) => (
                      <Mark key={week} week={week} check={checkOf(question, week, engine)} />
                    ))}
                  </span>
                </span>
              );
            })}
          </span>
        </summary>
        <div className="seo-q__body">
          {question.ask === '' ? null : (
            <p className="seo-q__ask">
              <span className="ink-3">Sorulan soru (İngilizce, değişmeden her hafta): </span>
              {question.ask}
            </p>
          )}
          <p className="seo-q__ask">
            <span className="ink-3">Aranan kelimeler: </span>
            <span className="mono">{question.query || '-'}</span>
            {question.locale === '' ? null : <span className="ink-3"> · pazar {question.locale}</span>}
          </p>
          {others.length === 0 ? null : (
            <p className="seo-q__ask">
              <span className="ink-3">Son hafta yerimizi alanlar: </span>
              {others.join(', ')}
            </p>
          )}
          <div className="seo-scroll">
            <table className="sx-table">
              <caption>{question.label}: hafta hafta</caption>
              <thead>
                <tr>
                  <th scope="col">Hafta</th>
                  {engines.map((engine) => (
                    <th key={engine} scope="col">
                      {SEO_ENGINE_LABELS[engine]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...weeks].reverse().map((week) => (
                  <tr key={week}>
                    <th scope="row">{formatIsoDay(week)}</th>
                    {engines.map((engine) => {
                      const check = checkOf(question, week, engine);
                      const words = checkWords(check);
                      return (
                        <td key={engine}>
                          <span className={`seo-pill seo-pill--${words.tone}`}>{words.text}</span>
                          {check?.hitUrl ? <span className="seo-q__hit mono"> {pathOf(check.hitUrl)}</span> : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {lastChecks.map((check) =>
            check?.detail ? (
              <p key={check.engine} className="seo-q__detail">
                <span className="ink-3">{SEO_ENGINE_LABELS[check.engine]}: </span>
                {check.detail}
              </p>
            ) : null,
          )}
        </div>
      </details>
    </li>
  );
}

function SearchTable({
  caption,
  first,
  rows,
  name,
  withPage,
}: {
  caption: string;
  first: string;
  rows: readonly SeoSearchRow[];
  name: (key: string) => string;
  withPage?: boolean;
}) {
  const [all, setAll] = useState(false);
  if (rows.length === 0) return <p className="sx-empty">Bu dönemde yok.</p>;
  const shown = all ? rows : rows.slice(0, ROWS_FIRST);
  return (
    <>
      <div className="seo-scroll">
        <table className="sx-table seo-table">
          <caption>{caption}</caption>
          <thead>
            <tr>
              <th scope="col">{first}</th>
              <th scope="col">Gösterim</th>
              <th scope="col">Tıklama</th>
              <th scope="col">Ort. sıra</th>
              {withPage ? <th scope="col">Gösterilen sayfa</th> : null}
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={row.key}>
                <th scope="row">{name(row.key)}</th>
                <td className="mono">
                  {n(row.impressions)} <Delta now={row.impressions} before={row.previousImpressions} />
                </td>
                <td className="mono">
                  {n(row.clicks)}
                  {row.ctr === null || row.clicks === 0 ? null : <span className="ink-3"> {pct(row.ctr)}</span>}
                </td>
                <td className="mono">
                  {pos(row.position)} <PosDelta now={row.position} before={row.previousPosition} />
                </td>
                {withPage ? (
                  <td className="seo-table__page mono">{row.page === null ? '-' : pathOf(row.page)}</td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > ROWS_FIRST ? (
        <button type="button" className="btn btn--secondary btn--small seo-more" onClick={() => setAll(!all)}>
          {all ? 'Daha az göster' : `Hepsini göster (${n(rows.length)})`}
        </button>
      ) : null}
    </>
  );
}

/** v0.1.208: clicks and impressions week by week, a bar per week scaled to the busiest one. */
function WeekTable({ caption, weeks }: { caption: string; weeks: readonly SeoSearchWeek[] }) {
  if (weeks.length === 0) return <p className="sx-empty">Bu dönemde yok.</p>;
  const most = Math.max(1, ...weeks.map((week) => week.impressions));
  return (
    <div className="seo-scroll">
      <table className="sx-table seo-table seo-weeks">
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Hafta</th>
            <th scope="col">Tıklama</th>
            <th scope="col">Gösterim</th>
            <th scope="col">Ort. sıra</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week.week}>
              <th scope="row">{formatIsoDay(week.week)}</th>
              <td className="mono">{n(week.clicks)}</td>
              <td className="mono">
                <span className="seo-weeks__bar" style={{ width: `${Math.round((week.impressions / most) * 100)}%` }} />
                <span className="seo-weeks__value">{n(week.impressions)}</span>
              </td>
              <td className="mono">{pos(week.position)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SearchSection({
  id,
  title,
  search,
  missing,
}: {
  id: string;
  title: string;
  search: SeoSearch | null;
  missing: string;
}) {
  return (
    <section aria-labelledby={`seo-${id}-h2`} className="sx-section">
      <h2 id={`seo-${id}-h2`} className="sx-section__title">
        {title}
      </h2>
      {search === null ? (
        <p className="sx-empty">{missing}</p>
      ) : (
        <div className="seo-grid">
          <Card
            id={`${id}-weeks`}
            title="Haftalık gidiş"
            note={
              id === 'google'
                ? 'Pazartesiden pazara; son hafta henüz bitmedi ve Google son iki günü geç verir.'
                : 'Pazartesiden pazara; son hafta henüz bitmedi.'
            }
          >
            <WeekTable caption={`${title}: haftalar`} weeks={search.weeks} />
          </Card>
          <Card
            id={`${id}-words`}
            title="Bizi bulduran kelimeler"
            note={`${formatIsoDay(search.from)} - ${formatIsoDay(search.to)}; yanındaki fark önceki ${SEO_SEARCH_DAYS} güne göre. Sıra küçüldükçe iyi (↑ yukarı çıktık).`}
          >
            <SearchTable
              caption={`${title}: kelimeler`}
              first="Kelime"
              rows={search.keywords}
              name={(key) => key}
              withPage={id === 'google'}
            />
          </Card>
          <Card id={`${id}-pages`} title="Sayfalar" note="Hangi sayfamız aramada kaç kez göründü.">
            <SearchTable caption={`${title}: sayfalar`} first="Sayfa" rows={search.pages} name={pathOf} />
          </Card>
          {search.countries.length === 0 ? null : (
            <Card id={`${id}-countries`} title="Ülkeler" note="Aramanın yapıldığı ülke.">
              <SearchTable
                caption={`${title}: ülkeler`}
                first="Ülke"
                rows={search.countries}
                name={(key) => COUNTRY_TR[key] ?? key.toUpperCase()}
              />
            </Card>
          )}
        </div>
      )}
    </section>
  );
}

/** Before and now of one gap, in words: Google position and impressions, or the AI answer and the search rank. */
function numbersOf(side: SeoChange['before']): string {
  if (side.position !== null || side.impressions !== null) {
    return `${side.position === null ? '-' : `${pos(side.position)}. sıra`} · ${n(side.impressions ?? 0)} gösterim · ${n(side.clicks ?? 0)} tık`;
  }
  const ai = side.found === null ? '-' : side.found ? 'anılıyoruz' : 'anılmıyoruz';
  return `yapay zekâ: ${ai} · arama: ${side.rank === null ? 'ilk 100’de yok' : `${side.rank}. sıra`}`;
}

/** v0.1.192: the SEO loop's gaps, what was done and what it changed. */
function ChangesSection({ changes }: { changes: readonly SeoChange[] }) {
  return (
    <section aria-labelledby="seo-changes-h" className="sx-section">
      <h2 id="seo-changes-h" className="sx-section__title">
        SEO döngüsü: yapılan değişiklikler ve etkisi
      </h2>
      {changes.length === 0 ? (
        <p className="sx-empty">
          Henüz değişiklik yok. Her pazartesi 09:40’ta en çok 3 boşluk seçilir (kelime 28 günde en az 30, rehber 56
          günde en az 100 gösterim almalı; aynı rehber 6 haftada bir), yayından 28 gün sonra sonucu değerlendirilir;
          İçerik-SEO rehber değişikliği hazırlar, Telegram’dan onaylarsın, site kendiliğinden yayına çıkar.
        </p>
      ) : (
        <div className="seo-scroll">
          <table className="sx-table seo-table">
            <caption>SEO döngüsünün hedefleri, önce ve şimdi</caption>
            <thead>
              <tr>
                <th scope="col">Hedef</th>
                <th scope="col">Durum</th>
                <th scope="col">Önce</th>
                <th scope="col">Şimdi</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((change) => (
                <tr key={change.targetId}>
                  <th scope="row">
                    <span className="seo-change__title">{change.title || change.path}</span>
                    <span className="seo-q__query mono">"{change.query}"</span>
                    <span className="ink-3">
                      {SEO_GAP_KIND_LABELS[change.kind] ?? change.kind} · {formatIsoDay(change.weekStart)} haftası
                    </span>
                  </th>
                  <td>
                    <span
                      className={`seo-pill seo-pill--${change.state === 'published' ? 'found' : change.state === 'failed' || change.state === 'rejected' ? 'error' : 'near'}`}
                    >
                      {SEO_CHANGE_STATE_LABELS[change.state]}
                    </span>
                    {change.publishedAt === null ? null : (
                      <span className="ink-3"> {formatIsoDay(change.publishedAt.slice(0, 10))}</span>
                    )}
                  </td>
                  <td className="seo-table__page">{numbersOf(change.before)}</td>
                  <td className="seo-table__page">
                    {change.state === 'published' ? numbersOf(change.now) : '-'}
                    {change.verdict === null ? null : (
                      <span className="ink-3">
                        {' '}
                        <span
                          className={`seo-pill seo-pill--${change.verdict === 'better' ? 'found' : change.verdict === 'worse' ? 'error' : 'near'}`}
                        >
                          {SEO_CHANGE_VERDICT_LABELS[change.verdict]}
                        </span>
                        {change.verdict === 'not_due' && change.checkOn !== null
                          ? ` · ${formatIsoDay(change.checkOn)} değerlendirilir`
                          : null}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function SeoPage() {
  const { store, state } = useSeoReport();
  const desktop = useIsDesktop();
  useEffect(() => {
    void store.reload();
  }, [store]);
  const view = state.view;
  if (view === null) {
    return state.error === null ? (
      <div className="loading" role="status">
        Yükleniyor…
      </div>
    ) : (
      <div className="shop-status" role="status">
        SEO raporu yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={() => void store.reload()}>
          Tekrar dene
        </button>
      </div>
    );
  }
  const engines = ENGINES.filter((engine) => view.summary.some((row) => row.engine === engine));
  // v0.1.214: the fixed twenty are the yardstick; the changing questions of the newest week get their own list.
  const fixed = view.questions.filter((question) => question.pool === 'core');
  const newest = view.weeks.at(-1);
  const changing = view.questions.filter(
    (question) => question.pool === 'rotating' && question.checks.some((check) => check.weekStart === newest),
  );
  const source = (id: 'search_console' | 'bing') => view.sources.find((row) => row.id === id);
  const missing = (id: 'search_console' | 'bing') => {
    const state = source(id);
    return state?.detailTr ?? 'Henüz veri yok.';
  };
  return (
    <div className={desktop ? 'au au--desk sx seo' : 'au sx seo'}>
      <Summary view={view} loading={state.loading} error={state.error} onReload={() => void store.reload()} />
      <FoundQueries view={view} />
      <BrandAnswers view={view} />
      {/* v0.1.209: the same numbers as the tables below, as charts. */}
      <section aria-labelledby="seo-charts-h" className="sx-section seo-charts">
        <h2 id="seo-charts-h" className="sx-section__title">
          Grafikler
        </h2>
        <div className="seo-grid">
          <Card id="chart-clicks" title="Google: haftalık tıklama" note="Aramadan siteye gelen tıklama, hafta hafta.">
            <WeeklyBars
              title="Google haftalık tıklama"
              weeks={view.google?.weeks ?? []}
              to={view.google?.to ?? null}
              measure="clicks"
              unit="tıklama"
            />
          </Card>
          <Card
            id="chart-impressions"
            title="Google: haftalık gösterim"
            note="Sitemizin arama sonuçlarında görünme sayısı."
          >
            <WeeklyBars
              title="Google haftalık gösterim"
              weeks={view.google?.weeks ?? []}
              to={view.google?.to ?? null}
              measure="impressions"
              unit="gösterim"
            />
          </Card>
          <Card id="chart-ai" title="Yapay zekâ testi: bizi bulan soru" note="Pazartesi testinde kaç soruda çıktık.">
            <AiFoundLines weeks={view.weeks} summary={view.summary} engines={engines} />
          </Card>
        </div>
      </section>
      <section aria-labelledby="seo-how-h" className="sx-section">
        <h2 id="seo-how-h" className="sx-section__title">
          Nasıl ölçülüyor
        </h2>
        <p className="seo-how">
          Her pazartesi bir alıcının soracağı 20 soru (bizim adımız geçmeden) iki yere sorulur.{' '}
          <b>{SEO_ENGINE_LABELS.gemini}</b>: cevap CadGaragePro’yu anıyor ya da sayfamızı kaynak gösteriyor mu.{' '}
          <b>{SEO_ENGINE_LABELS.brave}</b>: aranan kelimelerde sitemiz, Etsy mağazamız ya da ilanlarımız kaçıncı (ilk{' '}
          {SEO_TOP_RANK} “bulunuyoruz” sayılır). Satırdaki kareler haftalar: yeşil bulunuyoruz, sarı ilk {SEO_TOP_RANK}
          ’un dışında ama listede, gri yokuz. Satıra basınca sorunun tamamı, hafta hafta sonuç ve yerimizi alan siteler
          açılır. Altta Google ve Bing’in kendi verisi: insanların gerçekten aradığı ve bizi gösterdiği kelimeler.
        </p>
      </section>
      <ChangesSection changes={view.changes} />
      <section aria-labelledby="seo-q-h" className="sx-section">
        <h2 id="seo-q-h" className="sx-section__title">
          Sabit sorular ({n(fixed.length)})
        </h2>
        {view.weeks.length === 0 ? (
          <p className="sx-empty">Henüz sonuç yok: ilk test pazartesi 08:40’ta.</p>
        ) : (
          <ul className="seo-qs">
            {fixed.map((question) => (
              <QuestionRow key={question.id} question={question} weeks={view.weeks} engines={engines} />
            ))}
          </ul>
        )}
      </section>
      {changing.length === 0 ? null : (
        <section aria-labelledby="seo-rq-h" className="sx-section">
          <h2 id="seo-rq-h" className="sx-section__title">
            Bu haftanın değişen soruları ({n(changing.length)})
          </h2>
          <p className="seo-how">
            Her hafta değişir: Google ve Bing’de bizi gösteren aramalar ile katalogdaki araçlar ve dosya soruları. Sabit
            soruların sayıları bunlardan etkilenmez.
          </p>
          <ul className="seo-qs">
            {changing.map((question) => (
              <QuestionRow key={question.id} question={question} weeks={view.weeks} engines={engines} />
            ))}
          </ul>
        </section>
      )}
      {view.competitors.length === 0 ? null : (
        <section aria-labelledby="seo-comp-h" className="sx-section">
          <h2 id="seo-comp-h" className="sx-section__title">
            Yerimizi alanlar (son hafta)
          </h2>
          <Card
            id="comp"
            title="Cevaplarda ve sonuçlarda öne çıkan siteler"
            note="Kaç soruda alıcıyı oraya gönderdiler."
          >
            <ol className="sx-counts" aria-label="Yerimizi alan siteler">
              {view.competitors.map((row) => (
                <li key={row.domain} className="sx-counts__row">
                  <span className="sx-counts__key">{row.domain}</span>
                  <span className="sx-counts__value mono">{n(row.questions)} soru</span>
                </li>
              ))}
            </ol>
          </Card>
        </section>
      )}
      <SearchSection
        id="google"
        title={`Google araması (${SEO_SEARCH_DAYS} gün)`}
        search={view.google}
        missing={missing('search_console')}
      />
      <SearchSection
        id="bing"
        title={`Bing araması, yapay zekâ asistanı dahil (${SEO_SEARCH_DAYS} gün)`}
        search={view.bing}
        missing={missing('bing')}
      />
    </div>
  );
}
